import dataHubManager, { DataHubOperationType, DataHubStorageOperations } from '../../managers/data/DataHubManager'
import dataStorageManager, { BucketDefaultIDs } from '../../managers/data/DataStorageManager'
import { IArmador, OperatedArmador } from '../api-services/ArmadoresServices'
import { IChange, RequestedChange } from '../api-services/ChangesServices'
import { IEmbarcacion, OperatedEmbarcacion } from '../api-services/EmbarcacionesServices'
import { IProveedor } from '../api-services/ProveedoresServices'
import {
  IPermisoControl,
  IRepresentante,
  OperatedRepresentante,
  OperatedRepresentanteData,
  representanteData,
} from '../api-services/RepresentantesServices'
import { IRequest, RequestObjectType, RequestStatus } from '../api-services/RequestsServices'
import { ITripulante, OperatedTripulante } from '../api-services/TripulantesServices'
import { IUser } from '../api-services/UsersServices'
import { rutify } from '../apiUtils'
import { changesHubName } from './ChangesHub'
import dataHubServices, { defaultRefreshTime, StorageService } from './DataHubServices'

export enum RepresentantesHubPipelines {
  REPRESENTANTES_WITH_REQUESTS = 'representantes_with_requests', // Pipeline para mostrar los representantes con su solicitud relacionada
  REPRESENTANTES_WITH_PERMISO = 'representantes_with_permiso', // Pipeline para mostrar los representantes con su permiso relacionado
  REPRESENTANTES_WITH_EMPRESA = 'representantes_with_empresa', // Pipeline para mostrar los representantes con su empresa relacionada
  REPRESENTANTES_WITH_ARMADOR = 'representantes_with_armador', // Pipeline para mostrar los representantes con sus armadores relacionados (en caso de que el representante sea un tripulante relacionado a un armador, se mostrará la información del armador)
  REPRESENTANTES_WITH_TRIPULANTE = 'representantes_with_tripulante', // Pipeline para mostrar los representantes con sus tripulantes relacionados (en caso de que el representante sea un tripulante relacionado a un armador, se mostrará la información del tripulante)
  REPRESENTANTES_WITH_PERSONAL_REQUEST = 'representantes_with_personal_request', // Pipeline para mostrar los representantes con su solicitud relacionada a información personal
  REPRESENTANTES_FULL = 'representantes_full', // Pipeline completo para mostrar toda la información relacionada a un representante (solicitud, empresa, armador, tripulante, etc)
}

export const representantesHubName = 'representantesHub'

export class RepresentantesHub implements StorageService {
  bucketData: IUser[] = []

  storageOperations: DataHubStorageOperations<OperatedRepresentante> = {
    getData: () => [],
    markDirty: () => {},
    markStaled: () => {},
    forceRefresh: async () => {},
  }
  pipelinesOperations: Map<RepresentantesHubPipelines, DataHubStorageOperations<any>> = new Map()

  constructor() {}

  public async startup() {
    dataStorageManager.subscribeBucket<IUser>(BucketDefaultIDs.USERS, (data) => {
      this.bucketData = data
      this.storageOperations.forceRefresh()
    })
    this.storageOperations = dataHubManager.registerStorage(
      representantesHubName,
      async () => {
        const representantesData = this.bucketData.map((user) => {
          const representanteData: IRepresentante = {
            ...user,
            representados: [],
          }
          return representanteData
        })

        return representantesData
      },
      {
        invalidationStrategy: 'hybrid',
        checkTime: defaultRefreshTime,
        lazyLoad: true,
        pipelines: [
          {
            name: RepresentantesHubPipelines.REPRESENTANTES_WITH_REQUESTS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addRequests',
                execute: (data: IRepresentante[]) => {
                  const dataWithRequests = this.addRequestToRepresentantes(data)
                  return dataWithRequests
                },
              },
              {
                type: DataHubOperationType.FILTER,
                name: 'filterByRequestStatus',
                execute: (data: OperatedRepresentante[]) => {
                  const filteredData = data.filter((representante) => {
                    return representante.representados.length > 0
                  })
                  return filteredData
                },
              },
            ],
          },
          {
            name: RepresentantesHubPipelines.REPRESENTANTES_WITH_PERMISO,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addPermiso',
                execute: (data: OperatedRepresentante[]) => {
                  const dataWithPermiso = this.addPermisoToRepresentantes(data)
                  return dataWithPermiso
                },
              },
            ],
          },
          {
            name: RepresentantesHubPipelines.REPRESENTANTES_WITH_EMPRESA,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addEmpresa',
                execute: (data: OperatedRepresentante[]) => {
                  const dataWithEmpresa = this.addEmpresaToRepresentantes(data)
                  return dataWithEmpresa
                },
              },
            ],
          },
          {
            name: RepresentantesHubPipelines.REPRESENTANTES_WITH_ARMADOR,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addArmador',
                execute: (data: OperatedRepresentante[]) => {
                  const dataWithArmador = this.addArmadorToRepresentantes(data)
                  return dataWithArmador
                },
              },
            ],
          },
          {
            name: RepresentantesHubPipelines.REPRESENTANTES_WITH_TRIPULANTE,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addTripulante',
                execute: (data: OperatedRepresentante[]) => {
                  const dataWithTripulante = this.addTripulanteToRepresentantes(data)
                  return dataWithTripulante
                },
              },
            ],
          },
          {
            name: RepresentantesHubPipelines.REPRESENTANTES_WITH_PERSONAL_REQUEST,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addPersonalRequest',
                execute: (data: OperatedRepresentante[]) => {
                  const dataWithRequests = this.addPersonalRequestToRepresentantes(data)
                  return dataWithRequests
                },
              },
              {
                type: DataHubOperationType.MAP,
                name: 'personalRequestsSorted',
                execute: (data: OperatedRepresentante[]) => {
                  const sortedData = data.map((representante) => {
                    const sortedRequests = representante.usuarioPersonalRequests.sort((a, b) => {
                      return new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime()
                    })
                    return {
                      ...representante,
                      usuarioPersonalRequests: sortedRequests,
                    }
                  })
                  return sortedData
                },
              },
            ],
          },
          {
            name: RepresentantesHubPipelines.REPRESENTANTES_FULL,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'setFullData',
                execute: (data: OperatedRepresentante[]) => {
                  const fullData = this.setFullDataToRepresentantes(data)
                  return fullData
                },
              },
            ],
          },
        ],
      },
    )

    dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.PERMISOS, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.ARMADORES, () => {
      this.storageOperations.forceRefresh()
    })
    dataStorageManager.subscribeBucket(BucketDefaultIDs.PROVEEDORES, () => {
      this.storageOperations.forceRefresh()
    })

    dataHubServices.subscribeToHub(changesHubName, {
      subscriberID: 'representantesHub_changes_subscription',
      pipeline: null,
      callback: () => {
        console.log('Changes updated, refreshing embarcaciones data...')
        this.storageOperations.forceRefresh()
      },
    })
  }

  private addRequestToRepresentantes(representantes: IRepresentante[]): Array<OperatedRepresentante> {
    const requestBucketData = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const requestData = requestBucketData.filter(
      (request) =>
        request.tipo_objeto == RequestObjectType.RepresentanteArmador ||
        request.tipo_objeto == RequestObjectType.RepresentanteProveedor,
    )
    const operatedRepresentantes: Array<OperatedRepresentante> = representantes.map((representante) => {
      const solicitudes = requestData.filter((request) => {
        const id_usuario = parseInt(request.id_objeto_2 || '0')
        return id_usuario === representante.id_usuario
      })
      // Deduplicar por (tipo_objeto, id_objeto) conservando la más reciente por fecha_solicitud.
      // Múltiples solicitudes históricas (revocaciones + re-creaciones) para el mismo par
      // representante-empresa se colapsan en una sola representación con el estado vigente.
      const latestByEmpresa = new Map<string, IRequest>()
      for (const solicitud of solicitudes) {
        const key = `${solicitud.tipo_objeto}-${solicitud.id_objeto}`
        const existing = latestByEmpresa.get(key)
        if (!existing || new Date(solicitud.fecha_solicitud).getTime() > new Date(existing.fecha_solicitud).getTime()) {
          latestByEmpresa.set(key, solicitud)
        }
      }
      const rData: OperatedRepresentanteData[] = Array.from(latestByEmpresa.values()).map((solicitud) => {
        return {
          tipo: solicitud.tipo_objeto === RequestObjectType.RepresentanteArmador ? 'armador' : 'proveedor',
          id_empresa: parseInt(solicitud.id_objeto),
          empresaData: undefined,
          id_solicitud: solicitud.id,
          solicitudData: solicitud,
          permisos: [],
          permisosData: [],
        }
      })
      return {
        ...representante,
        representados: rData,
        representadosData: rData,
        usuarioArmadorData: undefined,
        usuarioTripulanteData: undefined,
        usuarioPersonalRequests: [],
      }
    })
    return operatedRepresentantes
  }

  private addPermisoToRepresentantes(representantes: OperatedRepresentante[]): Array<OperatedRepresentante> {
    const permisosList = dataStorageManager.getCurrentBucketData<IPermisoControl>(BucketDefaultIDs.PERMISOS) || []
    const operatedRepresentantes: Array<OperatedRepresentante> = representantes.map((item) => {
      const permisosData = item.representadosData.map((representado) => {
        const permisos = permisosList.filter((p) => Number(p.representante) === Number(representado.id_solicitud))
        return {
          ...representado,
          permisosData: permisos,
        }
      })
      return {
        ...item,
        representadosData: permisosData,
        representados: permisosData.map((p) => {
          return {
            tipo: p.tipo,
            id_empresa: p.id_empresa,
            id_solicitud: p.id_solicitud,
            permisos: p.permisosData.map((pd) => pd.id),
          }
        }),
      }
    })
    return operatedRepresentantes
  }

  private addEmpresaToRepresentantes(representantes: OperatedRepresentante[]): Array<OperatedRepresentante> {
    const armadoresList = dataStorageManager.getCurrentBucketData<IArmador>(BucketDefaultIDs.ARMADORES) || []
    const proveedoresList = dataStorageManager.getCurrentBucketData<IProveedor>(BucketDefaultIDs.PROVEEDORES) || []
    const operatedRepresentantes: Array<OperatedRepresentante> = representantes.map((item) => {
      const empresasData = item.representadosData.map((representado) => {
        const empresaData =
          representado.tipo === 'armador'
            ? armadoresList.find((a) => a.id === representado.id_empresa)
            : proveedoresList.find((p) => p.id === representado.id_empresa)
        return {
          ...representado,
          empresaData: empresaData,
        }
      })
      return {
        ...item,
        representadosData: empresasData,
        representados: empresasData.map((ed) => {
          return {
            tipo: ed.tipo,
            id_empresa: ed.id_empresa,
            id_solicitud: ed.id_solicitud,
            permisos: ed.permisos,
          }
        }),
      }
    })
    return operatedRepresentantes
  }

  private addArmadorToRepresentantes(representantes: OperatedRepresentante[]): Array<OperatedRepresentante> {
    const armadoresList = dataStorageManager.getCurrentBucketData<IArmador>(BucketDefaultIDs.ARMADORES) || []
    const operatedRepresentantes: Array<OperatedRepresentante> = representantes.map((item) => {
      // Solo armadores persona NATURAL: en un jurídico el rut_representante es el representante
      // legal (un apoderado), NO un armador. Sin este filtro el rep legal heredaba la "base
      // armador" del jurídico → aparecía con cargo "Armador" y dejaba de tratarse como apoderado.
      const armadorData: IArmador | undefined = armadoresList.find(
        (a) => a.tipo_persona === 'natural' && rutify(a.rut_representante) === rutify(item.rut),
      )
      if (armadorData) {
        item = {
          ...item,
          nombre_completo: armadorData.nombre_representante,
          email: armadorData.email_representante,
          telefono: armadorData.telefono_representante,
        }
      }

      return {
        ...item,
        usuarioArmadorData: armadorData,
      }
    })
    return operatedRepresentantes
  }

  private addTripulanteToRepresentantes(representantes: OperatedRepresentante[]): Array<OperatedRepresentante> {
    const tripulantesList = dataStorageManager.getCurrentBucketData<ITripulante>(BucketDefaultIDs.TRIPULANTES) || []
    const operatedRepresentantes: Array<OperatedRepresentante> = representantes.map((item) => {
      const tripulanteData: ITripulante | undefined = tripulantesList.find((t) => rutify(t.rut) === rutify(item.rut))
      if (tripulanteData && !item.usuarioArmadorData) {
        item = {
          ...item,
          nombre_completo: tripulanteData.nombre_completo,
          email: tripulanteData.email,
          telefono: tripulanteData.telefono,
          id_gestor_fotografia: tripulanteData.imagen_perfil,
          id_gestor_ci: tripulanteData.carnet_adjunto,
        }
      }
      return {
        ...item,
        usuarioTripulanteData: tripulanteData,
      }
    })
    return operatedRepresentantes
  }

  private addPersonalRequestToRepresentantes(representantes: OperatedRepresentante[]): Array<OperatedRepresentante> {
    const requestBucketData = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const tripulantesRequests = requestBucketData.filter(
      (request) => request.tipo_objeto == RequestObjectType.Tripulante,
    )
    const armadoresRequests = requestBucketData.filter((request) => request.tipo_objeto == RequestObjectType.Armador)
    const representantesRequests = requestBucketData.filter(
      (request) => request.tipo_objeto == RequestObjectType.Representante,
    )
    const operatedRepresentantes: Array<OperatedRepresentante> = representantes.map((representante) => {
      let tripulanteRequests: IRequest[] = []
      if (representante.usuarioTripulanteData) {
        tripulanteRequests = tripulantesRequests.filter((request) => {
          const id_tripulante = parseInt(request.id_objeto)
          return id_tripulante === representante.usuarioTripulanteData!.id
        })
      }
      let armadorRequests: IRequest[] = []
      if (representante.usuarioArmadorData) {
        armadorRequests = armadoresRequests.filter((request) => {
          const id_armador = parseInt(request.id_objeto)
          return id_armador === representante.usuarioArmadorData!.id
        })
      }
      const representantePersonalRequests = representantesRequests.filter((request) => {
        const id_usuario = parseInt(request.id_objeto)
        return id_usuario === representante.id_usuario
      })

      return {
        ...representante,
        usuarioPersonalRequests: [...representantePersonalRequests, ...armadorRequests, ...tripulanteRequests],
      }
    })
    return operatedRepresentantes
  }

  private setFullDataToRepresentantes(representantes: OperatedRepresentante[]): Array<OperatedRepresentante> {
    return representantes
  }
}
